import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import { PUBLIC_PATHS, SUBSCRIPTION_PATHS } from '../../utils/constants';
import '../../styles/SubscriptionModal.css';

/**
 * SubscriptionModal Component
 *
 * Blocking modal shown when app is locked.
 * Shows when:
 * - User has no subscription (can start trial or buy)
 * - User's subscription expired AND grace period is over
 *
 * Does NOT show when:
 * - User is in grace period (GracePeriodWarning handles this)
 * - User has active subscription
 * - User is on subscription-related or public pages
 */
const SubscriptionModal = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Context
    const {
        subscriptionData,
        isLocked,
        canStartTrial,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate,
        loading: contextLoading
    } = useSubscription();

    // API hook
    const { startTrial, error: apiError, clearError } = useSubscriptionApi();

    // Local state
    const [isStartingTrial, setIsStartingTrial] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    /**
     * Check if modal should be displayed
     */
    const shouldShowModal = useCallback(() => {
        // Don't show while loading
        if (contextLoading) return false;

        // Don't show on public/auth pages
        if (PUBLIC_PATHS.includes(location.pathname)) return false;

        // Don't show on subscription pages
        if (SUBSCRIPTION_PATHS.some(path => location.pathname.includes(path))) return false;

        // Show if locked
        return isLocked();
    }, [contextLoading, location.pathname, isLocked]);

    /**
     * Handle starting trial
     */
    const handleStartTrial = useCallback(async () => {
        try {
            setIsStartingTrial(true);
            setErrorMessage(null);
            clearError();

            await startTrial();
            await refreshSubscriptionStatus();
        } catch (err) {
            setErrorMessage(
                apiError ||
                err.message ||
                'Nie udało się rozpocząć okresu próbnego.'
            );
        } finally {
            setIsStartingTrial(false);
        }
    }, [startTrial, refreshSubscriptionStatus, clearError, apiError]);

    /**
     * Handle logout
     */
    const handleLogout = useCallback(() => {
        localStorage.removeItem('jwt');
        localStorage.removeItem('userType');
        sessionStorage.clear();
        navigate('/');
    }, [navigate]);

    /**
     * Clear error message
     */
    const handleClearError = () => {
        setErrorMessage(null);
        clearError();
    };

    // Don't render if not needed
    if (!shouldShowModal()) {
        return null;
    }

    const canTrial = canStartTrial();
    const status = subscriptionData?.status;

    return (
        <>
            {/* Main Modal */}
            <div className="subscription-modal-overlay">
                <div className="subscription-modal">
                    {/* Header */}
                    <div className="modal-header">
                        <div className="modal-icon">
                            {canTrial ? '🚀' : '🔒'}
                        </div>
                        <h2>
                            {canTrial
                                ? 'Witaj w PetBoard!'
                                : 'Subskrypcja nieaktywna'}
                        </h2>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {canTrial ? (
                            <TrialContent />
                        ) : (
                            <ExpiredContent
                                subscriptionData={subscriptionData}
                                status={status}
                                getStatusText={getSubscriptionStatusText}
                                formatDate={formatDate}
                            />
                        )}

                        {/* Error message */}
                        {errorMessage && (
                            <div className="error-message">
                                <p>{errorMessage}</p>
                                <button onClick={handleClearError}>×</button>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="modal-actions">
                        {canTrial && (
                            <button
                                className="primary-btn trial-btn"
                                onClick={handleStartTrial}
                                disabled={isStartingTrial}
                            >
                                {isStartingTrial
                                    ? 'Aktywowanie...'
                                    : 'Rozpocznij bezpłatny okres próbny'}
                            </button>
                        )}

                        <button
                            className={canTrial ? 'secondary-btn' : 'primary-btn'}
                            onClick={() => navigate('/subscription/plans')}
                        >
                            {canTrial ? 'Przeglądaj plany' : 'Wybierz plan subskrypcji'}
                        </button>

                        <button
                            className="text-btn logout-btn"
                            onClick={() => setShowLogoutConfirm(true)}
                        >
                            Wyloguj się
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <p>
                            To okno zostanie zamknięte automatycznie po aktywacji subskrypcji.
                        </p>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <LogoutConfirmModal
                    onConfirm={handleLogout}
                    onCancel={() => setShowLogoutConfirm(false)}
                />
            )}
        </>
    );
};

/**
 * Trial Content - shown for new users who can start trial
 */
const TrialContent = () => (
    <>
        <p className="modal-message">
            Rozpocznij 30-dniowy bezpłatny okres próbny, aby przetestować
            wszystkie funkcje aplikacji, lub wybierz plan subskrypcji.
        </p>

        <div className="trial-benefits">
            <h4>Co otrzymujesz w okresie próbnym:</h4>
            <ul>
                <li>Pełny dostęp do wszystkich funkcji</li>
                <li>Korzystanie z aplikacji przez 30 dni za darmo</li>
                <li>Brak zobowiązań - możesz anulować kiedy chcesz</li>
                <li>Wsparcie techniczne na adres e-mail: help@petboard.pl</li>
            </ul>
        </div>
    </>
);

/**
 * Expired Content - shown when subscription has expired
 */
const ExpiredContent = ({ subscriptionData, status, getStatusText, formatDate }) => (
    <>
        <p className="modal-message">
            Twoja subskrypcja wygasła. Odnów subskrypcję, aby kontynuować
            korzystanie z aplikacji.
        </p>

        {subscriptionData && (
            <div className="subscription-info">
                <p>
                    <strong>Status:</strong>{' '}
                    {getStatusText(status)}
                </p>
                {subscriptionData.endDate && (
                    <p>
                        <strong>Data wygaśnięcia:</strong>{' '}
                        {formatDate(subscriptionData.endDate)}
                    </p>
                )}
            </div>
        )}

        <div className="locked-features">
            <h4>Funkcje wymagające subskrypcji:</h4>
            <div className="features-grid">
                <span>Kalendarz treningów</span>
                <span>Zarządzanie psami</span>
                <span>Notatki z treningów</span>
                <span>Raporty i analizy</span>
            </div>
        </div>
    </>
);

/**
 * Logout Confirmation Modal
 */
const LogoutConfirmModal = ({ onConfirm, onCancel }) => (
    <div className="logout-confirm-overlay">
        <div className="logout-confirm-modal">
            <h3>Potwierdzenie wylogowania</h3>
            <p>Czy na pewno chcesz się wylogować?</p>
            <div className="logout-confirm-actions">
                <button onClick={onConfirm}>Tak, wyloguj</button>
                <button onClick={onCancel}>Anuluj</button>
            </div>
        </div>
    </div>
);

export default SubscriptionModal;
